import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { PageLoader } from '@/components/ui/Spinner';
import { RequireAdmin, RequireSystemAdmin } from './guards';
import { paths } from './paths';

// Lazy-load each page so the initial bundle stays small.
const LoginPage = lazy(() => import('@/pages/login/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const UsersListPage = lazy(() => import('@/pages/users/UsersListPage'));
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'));
const CompaniesListPage = lazy(() => import('@/pages/companies/CompaniesListPage'));
const CompanyDetailPage = lazy(() => import('@/pages/companies/CompanyDetailPage'));
const CountriesListPage = lazy(() => import('@/pages/countries/CountriesListPage'));
const CountryDetailPage = lazy(() => import('@/pages/countries/CountryDetailPage'));
const SafetyScoresListPage = lazy(
  () => import('@/pages/safety-scores/SafetyScoresListPage'),
);
const SafetyScoreDetailPage = lazy(
  () => import('@/pages/safety-scores/SafetyScoreDetailPage'),
);
const WeightsPage = lazy(() => import('@/pages/weights/WeightsPage'));
const SimulationPage = lazy(() => import('@/pages/simulation/SimulationPage'));
const KimiStatusPage = lazy(() => import('@/pages/kimi/KimiStatusPage'));
const NewsSourcesListPage = lazy(
  () => import('@/pages/news-sources/NewsSourcesListPage'),
);
const NewsSourceNewPage = lazy(() => import('@/pages/news-sources/NewsSourceNewPage'));
const PlansListPage = lazy(() => import('@/pages/plans/PlansListPage'));
const PlanNewPage = lazy(() => import('@/pages/plans/PlanNewPage'));
const PlanEditPage = lazy(() => import('@/pages/plans/PlanEditPage'));
const ConfigPage = lazy(() => import('@/pages/config/ConfigPage'));
const FeatureTogglesPage = lazy(
  () => import('@/pages/feature-toggles/FeatureTogglesPage'),
);
const BookingsAuditListPage = lazy(
  () => import('@/pages/bookings-audit/BookingsAuditListPage'),
);
const BookingDetailPage = lazy(
  () => import('@/pages/bookings-audit/BookingDetailPage'),
);
const PaymentsAuditListPage = lazy(
  () => import('@/pages/payments-audit/PaymentsAuditListPage'),
);
const PaymentDetailPage = lazy(
  () => import('@/pages/payments-audit/PaymentDetailPage'),
);
const SupportListPage = lazy(() => import('@/pages/support/SupportListPage'));
const SupportTicketPage = lazy(() => import('@/pages/support/SupportTicketPage'));
const PushBroadcastsPage = lazy(
  () => import('@/pages/push-broadcasts/PushBroadcastsPage'),
);
const AuditLogPage = lazy(() => import('@/pages/audit-log/AuditLogPage'));
const SystemPage = lazy(() => import('@/pages/system/SystemPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/NotFoundPage'));

const router = createBrowserRouter([
  {
    path: paths.login,
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAdmin>
        <AppShell />
      </RequireAdmin>
    ),
    children: [
      { index: true, element: lazyPage(<DashboardPage />) },
      { path: 'users', element: lazyPage(<UsersListPage />) },
      { path: 'users/:id', element: lazyPage(<UserDetailPage />) },
      {
        path: 'companies',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><CompaniesListPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'companies/:id',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><CompanyDetailPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'countries',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><CountriesListPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'countries/:code',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><CountryDetailPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      { path: 'safety-scores', element: lazyPage(<SafetyScoresListPage />) },
      { path: 'safety-scores/:code', element: lazyPage(<SafetyScoreDetailPage />) },
      {
        path: 'weights',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><WeightsPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'simulation',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><SimulationPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'kimi',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><KimiStatusPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'news-sources',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><NewsSourcesListPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'news-sources/new',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><NewsSourceNewPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'plans',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><PlansListPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'plans/new',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><PlanNewPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'plans/:code/edit',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><PlanEditPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'feature-toggles',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><FeatureTogglesPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      {
        path: 'config',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><ConfigPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      { path: 'bookings-audit', element: lazyPage(<BookingsAuditListPage />) },
      { path: 'bookings-audit/:id', element: lazyPage(<BookingDetailPage />) },
      { path: 'payments-audit', element: lazyPage(<PaymentsAuditListPage />) },
      { path: 'payments-audit/:id', element: lazyPage(<PaymentDetailPage />) },
      { path: 'support', element: lazyPage(<SupportListPage />) },
      { path: 'support/:id', element: lazyPage(<SupportTicketPage />) },
      {
        path: 'push-broadcasts',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><PushBroadcastsPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      { path: 'audit-log', element: lazyPage(<AuditLogPage />) },
      {
        path: 'system',
        element: (
          <RequireSystemAdmin>
            <Suspense fallback={<PageLoader />}><SystemPage /></Suspense>
          </RequireSystemAdmin>
        ),
      },
      { path: '*', element: <Navigate to={paths.notFound} replace /> },
    ],
  },
  {
    path: paths.notFound,
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

function lazyPage(node: React.ReactElement) {
  return <Suspense fallback={<PageLoader />}>{node}</Suspense>;
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
